import CustomButton from "@common/components/Button";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import { isLightEVCSMeeting } from "@common/utils/meetings";
import { canProduceMeeting } from "@common/utils/user";
import {
  CallEnd,
  Close,
  MoreTime,
  PersonAddAlt1,
  Sms,
} from "@mui/icons-material";
import { Box, Paper, Slide, IconButton, styled, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { McuType } from "@services/meetings/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, { useEffect, useState } from "react";
import ConfirmTerminateDialog from "../ConfirmTerminateDialog";
import ExtendDurationDrawer from "../Drawers/ExtendDuration";
import MessageAll from "../Drawers/MessageAll";
import AddParticipantDrawer from "./AddParticipantDrawer";

type ControlPanelProps = {
  open: boolean;
  anchorEl: null | HTMLDivElement;
  setOpen: Function;
};

const Button = styled(CustomButton)(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
  border: "unset",
}));

const ControlsDrawerType = {
  EXTEND_DURATION: "Extend Duration",
  ADD_PARTICIPANT: "Add Participant",
  MESSAGE_ALL: "Message All",
  RECORD: "Record",
};

const ControlPanel = (props: ControlPanelProps) => {
  const { open, anchorEl, setOpen } = props;
  const [user] = useAuthenticatedUser();
  const context = React.useContext(RoomsRealtimeContext);
  const dispatchError = useErrorNotification();
  const { meeting, loading } = { ...context.detail };
  const [openTerminate, setOpenTerminate] = useState<boolean>(false);
  const [bounds, setBounds] = useState<DOMRect | undefined>(
    anchorEl?.getBoundingClientRect()
  );
  const [openControlsDrawer, setControlsDrawer] = useState<string>("");
  const { palette } = useTheme();

  const getColor = (
    color: "error" | "success" | "background" | "primary" | "primaryLight"
  ) => {
    let res = {
      backgroundColor: "",
      color: palette.common.white,
      "&:hover": {
        backgroundColor: "",
      },
    };
    switch (color) {
      case "error": {
        res.backgroundColor = palette.error.dark;
        res["&:hover"].backgroundColor = palette.error.dark;
        break;
      }
      case "success": {
        res.backgroundColor = palette.success.dark;
        res["&:hover"].backgroundColor = palette.success.dark;
        break;
      }
      case "background": {
        res.backgroundColor = palette.background.default;
        res["&:hover"].backgroundColor = palette.background.default;
        break;
      }
      case "primary": {
        res.backgroundColor = palette.primary.dark;
        res["&:hover"].backgroundColor = palette.primary.dark;
        break;
      }
      case "primaryLight": {
        res.backgroundColor = palette.primary.light;
        res.color = palette.common.black;
        res["&:hover"].backgroundColor = palette.primary.light;
        break;
      }
      default:
        break;
    }
    return res;
  };

  const closeTerminateDialog = (validate: boolean) => {
    validate && setOpen(false);
    setOpenTerminate(false);
  };
  useEffect(() => {
    setBounds(anchorEl?.getBoundingClientRect());
  }, [anchorEl?.getBoundingClientRect().width]);

  return (
    <Box
      sx={{
        position: "absolute",
        overflow: "hidden",
        width: bounds?.width,
        height: bounds?.height,
      }}
    >
      <ExtendDurationDrawer
        open={openControlsDrawer === ControlsDrawerType.EXTEND_DURATION}
        setOpen={() => setControlsDrawer("")}
      />
      <MessageAll
        open={openControlsDrawer === ControlsDrawerType.MESSAGE_ALL}
        onClose={() => setControlsDrawer("")}
      />
      <AddParticipantDrawer
        meeting={meeting}
        open={openControlsDrawer === ControlsDrawerType.ADD_PARTICIPANT}
        setOpen={() => setControlsDrawer("")}
      />
      <Slide
        in={open}
        direction="right"
        container={anchorEl}
        onExited={() => setOpen(false)}
        timeout={500}
      >
        <Box
          sx={{
            position: "absolute",
            zIndex: 1,
            height: "100%",
            width: "100%",
            padding: (theme) => theme.spacing(1, 0),
          }}
        >
          <Paper sx={{ height: "100%", backgroundImage: "none" }}>
            <ConfirmTerminateDialog
              open={openTerminate}
              onConfirm={closeTerminateDialog}
            />
            <Grid
              container
              direction="row"
              sx={{ width: '100%', gap: "1rem",
                justifyContent: "space-between",
                alignItems: "center",
                height: "100%",
                paddingX: "1rem", }}
            >
              <Grid
                sx={{
                  flexGrow: "1",
                }}>
                <Grid
                  sx={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                    display: "grid",
                    gap: "1rem",
                  }}
                >
                  <Button
                    sx={{ ...getColor("error") }}
                    size="medium"
                    startIcon={<CallEnd />}
                    onClick={() => setOpenTerminate(true)}
                    loading={loading}
                    disabled={
                      isLightEVCSMeeting(meeting) || meeting?.monitoringOnly
                    }
                  >
                    Terminate
                  </Button>
                  <Button
                    sx={{ ...getColor("success") }}
                    size="medium"
                    startIcon={<PersonAddAlt1 />}
                    onClick={() =>
                      setControlsDrawer(ControlsDrawerType.ADD_PARTICIPANT)
                    }
                    disabled={loading}
                  >
                    Add Participant
                  </Button>
                  <Button
                    sx={{ ...getColor("background") }}
                    size="medium"
                    startIcon={<MoreTime />}
                    onClick={() =>
                      setControlsDrawer(ControlsDrawerType.EXTEND_DURATION)
                    }
                    loading={loading}
                    disabled={
                      isLightEVCSMeeting(meeting) ||
                      meeting?.scheduledMeeting === undefined ||
                      meeting?.monitoringOnly
                    }
                  >
                    Extend Duration
                  </Button>
                  <Button
                    sx={{ ...getColor("background") }}
                    size="medium"
                    startIcon={<Sms />}
                    onClick={() =>
                      setControlsDrawer(ControlsDrawerType.MESSAGE_ALL)
                    }
                    loading={loading}
                    disabled={
                      (meeting?.mcuType !== McuType.RMX &&
                        meeting?.mcuType !== McuType.DMA) ||
                      meeting?.monitoringOnly
                    }
                  >
                    Message All
                  </Button>
                </Grid>
              </Grid>
              <IconButton onClick={() => setOpen(false)}>
                <Close />
              </IconButton>
            </Grid>
          </Paper>
        </Box>
      </Slide>
    </Box>
  );
};

export default ControlPanel;
